import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  FileText, Calendar, Building2, Tag,
  AlertTriangle, ShieldCheck, ShieldAlert,
  ChevronDown, ChevronRight, Download, Loader2, Package,
  Mail, CheckCircle, XCircle, Clock, FilePlus2, Folder, FolderOpen, Send,
} from 'lucide-react';
import api from '../../api/auth.api';

// ============================================================================
// BiddingDetail — read-only project detail for PWA admins.
// Sections (top to bottom):
//   1. Header with urgency badge
//   2. Plans (file list + Download all ZIP + plans_link)
//   3. Addenda (if any)
//   4. GCs (collapsed list)
//   5. By trade — accordion; each trade row taps into a contact sheet
// ============================================================================

interface BidProjectFile {
  project_file_id: number;
  display_name: string;
  relative_path?: string | null;
  content_type: string | null;
  size_bytes: number | null;
  uploaded_at: string;
}
interface BidAddendum {
  addendum_id: number;
  addendum_number: number;
  description: string | null;
  created_at: string;
  notified_at: string | null;
  notified_count: number | null;
  files: BidProjectFile[];
}
interface BidProjectGc {
  project_gc_id: number;
  gc_name: string;
  gc_contact_name: string | null;
  gc_contact_email: string | null;
  bid_due_date_to_gc: string | null;
  contractor_name: string | null;
}
interface BidInvite {
  invite_id: number;
  contact_id: number;
  rsvp_status: 'pending' | 'will_bid' | 'declining' | 'bid_submitted';
  rsvp_at: string | null;
  sent_at: string | null;
  contact_name: string | null;
  contact_email: string | null;
  unsubscribed_at: string | null;
  subcontractor_id: number;
  company_name: string;
  sub_task_type_codes: string | null;   // the sub's declared trades (JSON array)
}
interface BidProjectDetail {
  project: {
    bid_project_id: number;
    project_name: string;
    external_project_number: string | null;
    location: string | null;
    description: string | null;
    sub_bid_due_date: string | null;
    plans_link: string | null;
    is_archived: boolean;
    notes: string | null;
    trades: string | null;
  };
  gcs: BidProjectGc[];
  invites: BidInvite[];
  files: BidProjectFile[];
  addenda: BidAddendum[];
}

interface TradeType { type_code: string; type_name: string; is_active: boolean; }

const colors = {
  bg: '#f8fafc', cardBg: '#fff',
  text: '#1e293b', textMuted: '#64748b', textFaint: '#94a3b8',
  border: '#e2e8f0',
  primary: '#3b82f6',
  critical: '#dc2626', criticalBg: '#fee2e2',
  warn: '#a16207', warnBg: '#fef3c7',
  ok: '#059669', okBg: '#dcfce7',
};

function parseTrades(raw: any): string[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(x => typeof x === 'string');
  try { const v = JSON.parse(raw); return Array.isArray(v) ? v.filter(x => typeof x === 'string') : []; }
  catch { return []; }
}

function daysUntil(iso: string): number | null {
  const due = new Date(iso);
  if (Number.isNaN(due.getTime())) return null;
  const start = new Date(); start.setHours(0, 0, 0, 0);
  return Math.round((due.getTime() - start.getTime()) / 86400000);
}

function dueLabel(d: string | null): { text: string; color: string; bg: string } | null {
  if (!d) return null;
  const days = daysUntil(d);
  if (days === null) return null;
  if (days < 0)   return { text: `${Math.abs(days)}d OVERDUE`, color: '#991b1b', bg: '#fee2e2' };
  if (days === 0) return { text: 'DUE TODAY',                  color: '#9a3412', bg: '#fef3c7' };
  if (days === 1) return { text: 'due tomorrow',               color: '#9a3412', bg: '#fef3c7' };
  if (days <= 7)  return { text: `due in ${days}d`,            color: '#a16207', bg: '#fef3c7' };
  return { text: `due in ${days}d`, color: colors.textMuted, bg: colors.bg };
}

function fmtBytes(n: number | null | undefined): string {
  if (!n || n <= 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = n, u = 0;
  while (v >= 1024 && u < units.length - 1) { v /= 1024; u++; }
  return `${v.toFixed(v < 10 && u > 0 ? 1 : 0)} ${units[u]}`;
}

export const BiddingDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const pid = id ? parseInt(id, 10) : 0;
  const [data, setData] = useState<BidProjectDetail | null>(null);
  const [tradeCatalog, setTradeCatalog] = useState<TradeType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [zipping, setZipping] = useState(false);
  const [zipLoaded, setZipLoaded] = useState(0);
  const [downloadingFile, setDownloadingFile] = useState<number | null>(null);
  const [openTrades, setOpenTrades] = useState<Set<string>>(new Set());

  useEffect(() => {
    (async () => {
      setLoading(true); setError(null);
      try {
        const [d, t] = await Promise.all([
          api.get(`/api/planroom/pwa/projects/${pid}`),
          // Same catalog bfav5 uses — STARTUP_TASK_TYPES, where bid-room
          // trade codes live (SUBCONTRACTOR_TYPES is a different, older
          // table and its lookup misses → raw codes like trucking_hauling).
          api.get(`/api/job-startup/task-types`).catch(() => ({ data: { data: [] } })),
        ]);
        setData(d.data?.data);
        const types = Array.isArray(t.data?.data) ? t.data.data : (Array.isArray(t.data) ? t.data : []);
        setTradeCatalog(types);
      } catch (e: any) {
        setError(e?.response?.data?.error || 'Could not load project');
      } finally { setLoading(false); }
    })();
  }, [pid]);

  const projectTrades = parseTrades(data?.project?.trades);
  const tradeName = useMemo(() => new Map(tradeCatalog.map(t => [t.type_code, t.type_name])), [tradeCatalog]);

  // Open one file. The tab must be opened SYNCHRONOUSLY (inside the tap
  // gesture, or popup blockers eat it), so we open it immediately with our
  // own "Downloading" page and swap in the signed URL when it arrives —
  // otherwise the user stares at a dark blank tab and assumes an error.
  const openFile = async (f: BidProjectFile) => {
    setDownloadingFile(f.project_file_id);
    const w = window.open('', '_blank');
    if (w) {
      w.document.write(`
        <html><head><title>${f.display_name.replace(/</g, '&lt;')}</title>
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <style>
          body { margin:0; min-height:100vh; display:flex; align-items:center; justify-content:center;
                 background:#f8fafc; font-family:-apple-system,system-ui,sans-serif; }
          .box { text-align:center; color:#334155; padding:24px; }
          .spin { width:36px; height:36px; margin:0 auto 16px; border:4px solid #e2e8f0;
                  border-top-color:#3b82f6; border-radius:50%; animation:r 0.8s linear infinite; }
          @keyframes r { to { transform:rotate(360deg); } }
          .n { font-size:15px; font-weight:600; word-break:break-word; max-width:320px; }
          .s { font-size:13px; color:#64748b; margin-top:6px; }
        </style></head>
        <body><div class="box">
          <div class="spin"></div>
          <div class="n">${f.display_name.replace(/</g, '&lt;')}</div>
          <div class="s">Downloading — large sheets can take a moment…</div>
        </div></body></html>`);
      w.document.close();
    }
    try {
      const r = await api.get(`/api/planroom/pwa/files/${f.project_file_id}/download`);
      const url = r.data?.data?.url;
      if (url && w) w.location.href = url;
      else if (url) window.open(url, '_blank', 'noopener,noreferrer');
      else if (w) w.close();
    } catch (e: any) {
      if (w) w.close();
      alert(e?.response?.data?.error || 'Could not open file');
    } finally { setDownloadingFile(null); }
  };

  // Download all plans + addenda as one ZIP. Fetch as blob, then trigger
  // a Blob download via temporary anchor.
  const downloadZip = async () => {
    setZipping(true);
    setZipLoaded(0);
    try {
      // timeout: 0 — the global 7s client timeout would abort any real plan
      // set; the server streams the zip as it builds it (can take minutes).
      const r = await api.get(`/api/planroom/pwa/projects/${pid}/zip`, {
        responseType: 'blob', timeout: 0,
        onDownloadProgress: (e: any) => setZipLoaded(e.loaded || 0),
      });
      const cd = (r.headers as any)['content-disposition'] || '';
      const m = /filename="?([^";]+)"?/.exec(cd);
      const filename = m?.[1] || `plans-${pid}.zip`;
      const blobUrl = URL.createObjectURL(r.data as Blob);
      const a = document.createElement('a'); a.href = blobUrl; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (e: any) {
      alert('Could not build ZIP — try downloading files individually.');
    } finally { setZipping(false); }
  };

  // Per-trade summary: how many companies invited, how many will_bid, etc.
  // Counts are per COMPANY, not per contact — two estimators at one sub are a
  // single bidder, so counting contacts would over-count (false positives).
  type CompanyGroup = { subcontractor_id: number; company_name: string; status: BidInvite['rsvp_status']; contacts: BidInvite[] };
  type TradeRow = { code: string; name: string; companies: CompanyGroup[]; willBid: number; submitted: number; pending: number; declining: number; severity: 'covered' | 'no_commit' | 'no_invites' };
  const tradeRows: TradeRow[] = useMemo(() => {
    if (!data) return [];
    return projectTrades.map(code => {
      // Show only the subs whose declared trades (SUBCONTRACTORS.task_type_codes,
      // returned per-invite as sub_task_type_codes) include this trade — so each
      // trade row lists the subs actually invited for that trade, not everyone.
      const invited = data.invites.filter(i => parseTrades(i.sub_task_type_codes).includes(code));
      // Group contacts by company so each sub counts once.
      const byCompany = new Map<number, BidInvite[]>();
      for (const i of invited) {
        const arr = byCompany.get(i.subcontractor_id) || [];
        arr.push(i);
        byCompany.set(i.subcontractor_id, arr);
      }
      const companies: CompanyGroup[] = [...byCompany.values()].map(contacts => ({
        subcontractor_id: contacts[0].subcontractor_id,
        company_name: contacts[0].company_name,
        // A company is "submitted" if ANY contact marked the bid in; it "will
        // bid" if any contact will; it's "declining" only when every contact
        // declined; otherwise it's still pending.
        status: contacts.some(c => c.rsvp_status === 'bid_submitted') ? 'bid_submitted'
              : contacts.some(c => c.rsvp_status === 'will_bid') ? 'will_bid'
              : contacts.some(c => c.rsvp_status === 'pending') ? 'pending'
              : 'declining',
        contacts,
      }));
      const willBid   = companies.filter(c => c.status === 'will_bid').length;
      const submitted = companies.filter(c => c.status === 'bid_submitted').length;
      const pending   = companies.filter(c => c.status === 'pending').length;
      const declining = companies.filter(c => c.status === 'declining').length;
      let severity: TradeRow['severity'];
      if (companies.length === 0) severity = 'no_invites';
      else if (willBid === 0 && submitted === 0) severity = 'no_commit';
      else                         severity = 'covered';
      return { code, name: tradeName.get(code) || code, companies, willBid, submitted, pending, declining, severity };
    });
  }, [data, projectTrades, tradeName]);

  if (loading) return <div style={{ minHeight: '100vh', background: colors.bg, padding: 24, color: colors.textMuted, fontSize: 15 }}>Loading…</div>;
  if (error || !data) return (
    <div style={{ minHeight: '100vh', background: colors.bg, padding: 24 }}>
      <p style={{ color: colors.critical, fontSize: 15 }}>{error || 'Project not found'}</p>
    </div>
  );

  const p = data.project;
  const due = dueLabel(p.sub_bid_due_date);

  return (
    <div style={{ minHeight: '100vh', background: colors.bg }}>
      {/* Title bar (no back button — PWA's global Header handles that) */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 10,
        background: colors.cardBg, borderBottom: `1px solid ${colors.border}`,
        padding: '14px 16px',
      }}>
        <h1 style={{
          margin: 0, fontSize: 20, fontWeight: 800, color: colors.text,
          letterSpacing: -0.3,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {p.project_name}
        </h1>
      </div>

      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Project info */}
        <Card>
          <div style={{ fontSize: 14, color: colors.textMuted, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {p.location && <span>{p.location}</span>}
            {p.external_project_number && <span>· #{p.external_project_number}</span>}
          </div>
          {p.sub_bid_due_date && (
            <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 15, color: colors.text, fontWeight: 600 }}>
                <Calendar size={14} style={{ verticalAlign: 'middle', marginRight: 5 }} />
                Bid due {new Date(p.sub_bid_due_date).toLocaleDateString(undefined, { timeZone: 'UTC' })}
              </span>
              {due && <span style={{ ...pillStyle, background: due.bg, color: due.color }}>{due.text}</span>}
            </div>
          )}
          {p.description && <p style={{ marginTop: 12, fontSize: 15, color: colors.text, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{p.description}</p>}
        </Card>

        {/* Plans */}
        <Card>
          <SectionTitle icon={<FileText size={16} />} text="Plans" count={data.files.length} />
          {data.files.length === 0 && !p.plans_link ? (
            <p style={{ fontSize: 13, color: colors.textMuted, margin: 0 }}>No plans posted yet.</p>
          ) : (
            <>
              {data.files.length >= 2 && (
                <button onClick={downloadZip} disabled={zipping} style={zipBtn}>
                  {zipping ? <Loader2 size={14} className="spin" /> : <Package size={14} />}
                  {zipping
                    ? (zipLoaded > 0 ? `Downloading… ${fmtBytes(zipLoaded)}` : 'Building ZIP…')
                    : `Download all (${data.files.length} files)`}
                </button>
              )}
              <FolderGroupedFiles files={data.files} openFile={openFile} downloadingFile={downloadingFile} />
              {p.plans_link && (
                <a href={p.plans_link} target="_blank" rel="noopener noreferrer" style={{
                  display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, padding: '10px 12px',
                  background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6,
                  color: colors.primary, fontSize: 13, fontWeight: 500, textDecoration: 'none',
                }}>
                  <Download size={14} /> Open external plans link
                </a>
              )}
            </>
          )}
        </Card>

        {/* Addenda */}
        {data.addenda.length > 0 && (
          <Card>
            <SectionTitle icon={<FilePlus2 size={16} />} text="Addenda" count={data.addenda.length} />
            {data.addenda.map(a => (
              <div key={a.addendum_id} style={{
                paddingBottom: 12, marginBottom: 12,
                borderBottom: `1px solid ${colors.border}`,
              }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
                  <span style={{ ...pillStyle, background: colors.warnBg, color: '#92400e' }}>
                    ADDENDUM {a.addendum_number}
                  </span>
                  {a.notified_at && (
                    <span style={{ fontSize: 11, color: colors.textMuted }}>
                      posted {new Date(a.notified_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
                {a.description && <p style={{ fontSize: 13, margin: '0 0 8px', color: colors.text }}>{a.description}</p>}
                {a.files.map((f, i) => <FileRow key={f.project_file_id} f={f} last={i === a.files.length - 1} onOpen={() => openFile(f)} loading={downloadingFile === f.project_file_id} />)}
              </div>
            ))}
          </Card>
        )}

        {/* GCs */}
        {data.gcs.length > 0 && (
          <Card>
            <SectionTitle icon={<Building2 size={16} />} text="General contractors" count={data.gcs.length} />
            {data.gcs.map((gc, i) => (
              <div key={gc.project_gc_id} style={{
                padding: '10px 0',
                borderBottom: i < data.gcs.length - 1 ? `1px solid ${colors.border}` : 'none',
              }}>
                <div style={{ color: colors.text, fontWeight: 700, fontSize: 16 }}>{gc.contractor_name || gc.gc_name}</div>
                <div style={{ fontSize: 14, color: colors.textMuted, marginTop: 4 }}>
                  {gc.gc_contact_name || '—'}{gc.gc_contact_email ? ` · ${gc.gc_contact_email}` : ''}
                </div>
                {gc.bid_due_date_to_gc && (
                  <div style={{ fontSize: 14, color: colors.textMuted, marginTop: 6 }}>
                    Our bid due: <strong style={{ color: colors.text }}>{new Date(gc.bid_due_date_to_gc).toLocaleDateString()}</strong>
                  </div>
                )}
              </div>
            ))}
          </Card>
        )}

        {/* By trade */}
        {projectTrades.length > 0 && (
          <Card>
            <SectionTitle icon={<Tag size={16} />} text="By trade" count={projectTrades.length} />
            {tradeRows.map(r => {
              const open = openTrades.has(r.code);
              return (
                <div key={r.code} style={{ borderTop: `1px solid ${colors.border}`, padding: '14px 0' }}>
                  <button
                    onClick={() => setOpenTrades(s => { const n = new Set(s); n.has(r.code) ? n.delete(r.code) : n.add(r.code); return n; })}
                    style={{
                      width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 10,
                    }}
                  >
                    {open ? <ChevronDown size={18} color={colors.textFaint} /> : <ChevronRight size={18} color={colors.textFaint} />}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, color: colors.text, fontSize: 16 }}>{r.name}</div>
                      <div style={{ fontSize: 13, color: colors.textMuted, marginTop: 4 }}>
                        {r.submitted > 0 && <span style={{ color: '#1d4ed8', marginRight: 10, fontWeight: 600 }}>{r.submitted} submitted</span>}
                        {r.willBid > 0 && <span style={{ color: colors.ok, marginRight: 10, fontWeight: 600 }}>{r.willBid} will bid</span>}
                        {r.pending > 0 && <span style={{ color: colors.warn, marginRight: 10, fontWeight: 600 }}>{r.pending} pending</span>}
                        {r.declining > 0 && <span style={{ color: colors.critical, fontWeight: 600 }}>{r.declining} decline</span>}
                        {r.companies.length === 0 && <span style={{ fontStyle: 'italic' }}>No invites yet</span>}
                      </div>
                    </div>
                    <SeverityPill s={r.severity} />
                  </button>

                  {/* Contact cards render inline (no extra click) */}
                  {open && (
                    <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {r.companies.length === 0 ? (
                        <p style={{ padding: '12px', fontSize: 14, color: colors.textMuted, textAlign: 'center', background: colors.bg, borderRadius: 8, margin: 0 }}>
                          No subs invited for this trade yet.
                        </p>
                      ) : r.companies.map(co => <CompanyCard key={co.subcontractor_id} co={co} />)}
                    </div>
                  )}
                </div>
              );
            })}
          </Card>
        )}
      </div>
    </div>
  );
};

// ----- shared sub-components --------------------------------------------------

const pillStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700,
  textTransform: 'uppercase', letterSpacing: 0.4,
};

const zipBtn: React.CSSProperties = {
  width: '100%', marginBottom: 10, padding: '10px 12px',
  background: colors.primary, color: '#fff', border: 'none', borderRadius: 6,
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  fontSize: 14, fontWeight: 600, cursor: 'pointer',
};

const Card: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ background: colors.cardBg, border: `1px solid ${colors.border}`, borderRadius: 10, padding: 14 }}>
    {children}
  </div>
);

const SectionTitle: React.FC<{ icon: React.ReactNode; text: string; count?: number }> = ({ icon, text, count }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 7,
    fontSize: 15, fontWeight: 800, color: colors.text,
    letterSpacing: -0.1, marginBottom: 12,
    paddingBottom: 10, borderBottom: `2px solid ${colors.border}`,
  }}>
    {icon} {text}
    {count != null && (
      <span style={{ color: colors.textMuted, fontWeight: 600, fontSize: 14 }}>
        ({count})
      </span>
    )}
  </div>
);

const FileRow: React.FC<{ f: BidProjectFile; last: boolean; onOpen: () => void; loading: boolean }> = ({ f, last, onOpen, loading }) => (
  <button
    onClick={onOpen}
    disabled={loading}
    style={{
      width: '100%', textAlign: 'left',
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '12px 0', borderBottom: last ? 'none' : `1px solid ${colors.border}`,
      background: 'none', border: 'none', cursor: loading ? 'wait' : 'pointer',
    }}
  >
    <FileText size={16} color={colors.textFaint} style={{ flexShrink: 0 }} />
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{
        color: colors.primary, textDecoration: 'underline',
        textDecorationThickness: 1, textUnderlineOffset: 2,
        fontSize: 15, fontWeight: 600,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {f.display_name}
      </div>
      <div style={{ fontSize: 13, color: colors.textMuted, marginTop: 3 }}>
        {fmtBytes(f.size_bytes)}
      </div>
    </div>
    {loading
      ? <Loader2 size={16} color={colors.textFaint} />
      : <Download size={16} color={colors.textFaint} />}
  </button>
);

// Plan files grouped by relative_path (Dropbox imports / folder uploads).
// Folders collapse by default — tap to expand. Root files list directly when
// there are only a few; big flat imports (e.g. a 171-file Dropbox folder with
// no subfolders) collapse behind a "Plan files (N)" toggle so the page isn't
// one endless scroll.
const ROOT_COLLAPSE_THRESHOLD = 8;
const FolderGroupedFiles: React.FC<{
  files: BidProjectFile[];
  openFile: (f: BidProjectFile) => void;
  downloadingFile: number | null;
}> = ({ files, openFile, downloadingFile }) => {
  const [open, setOpen] = useState<Set<string>>(new Set());
  const groups = useMemo(() => {
    const m = new Map<string, BidProjectFile[]>();
    for (const f of files) {
      const k = f.relative_path || '';
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(f);
    }
    for (const arr of m.values()) arr.sort((a, b) => a.display_name.localeCompare(b.display_name));
    return m;
  }, [files]);
  const folders = [...groups.keys()].filter(k => k !== '').sort();
  const rootFiles = groups.get('') || [];
  const rootCollapsible = rootFiles.length > ROOT_COLLAPSE_THRESHOLD;
  const rootOpen = open.has('');
  const toggle = (k: string) => setOpen(prev => {
    const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n;
  });
  return (
    <div>
      {rootCollapsible ? (
        <div>
          <button
            onClick={() => toggle('')}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, width: '100%',
              padding: '12px 4px', background: 'none', border: 'none',
              borderBottom: `1px solid ${colors.border}`, cursor: 'pointer', textAlign: 'left',
            }}
          >
            {rootOpen ? <ChevronDown size={15} color={colors.textFaint} /> : <ChevronRight size={15} color={colors.textFaint} />}
            {rootOpen
              ? <FolderOpen size={16} color="#d97706" style={{ flexShrink: 0 }} />
              : <Folder size={16} color="#d97706" style={{ flexShrink: 0 }} />}
            <span style={{
              flex: 1, minWidth: 0, fontSize: 15, fontWeight: 600, color: colors.text,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              Plan files
            </span>
            <span style={{ fontSize: 12, color: colors.textFaint, flexShrink: 0 }}>{rootFiles.length}</span>
          </button>
          {rootOpen && (
            <div style={{ paddingLeft: 14 }}>
              {rootFiles.map((f, i) => (
                <FileRow key={f.project_file_id} f={f}
                  last={folders.length === 0 && i === rootFiles.length - 1}
                  onOpen={() => openFile(f)} loading={downloadingFile === f.project_file_id} />
              ))}
            </div>
          )}
        </div>
      ) : rootFiles.map((f, i) => (
        <FileRow key={f.project_file_id} f={f}
          last={folders.length === 0 && i === rootFiles.length - 1}
          onOpen={() => openFile(f)} loading={downloadingFile === f.project_file_id} />
      ))}
      {folders.map(k => {
        const isOpen = open.has(k);
        const arr = groups.get(k)!;
        return (
          <div key={k}>
            <button
              onClick={() => toggle(k)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                padding: '12px 4px', background: 'none', border: 'none',
                borderBottom: `1px solid ${colors.border}`, cursor: 'pointer', textAlign: 'left',
              }}
            >
              {isOpen ? <ChevronDown size={15} color={colors.textFaint} /> : <ChevronRight size={15} color={colors.textFaint} />}
              {isOpen
                ? <FolderOpen size={16} color="#d97706" style={{ flexShrink: 0 }} />
                : <Folder size={16} color="#d97706" style={{ flexShrink: 0 }} />}
              <span style={{
                flex: 1, minWidth: 0, fontSize: 15, fontWeight: 600, color: colors.text,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {k}
              </span>
              <span style={{ fontSize: 12, color: colors.textFaint, flexShrink: 0 }}>{arr.length}</span>
            </button>
            {isOpen && (
              <div style={{ paddingLeft: 14 }}>
                {arr.map((f, i) => (
                  <FileRow key={f.project_file_id} f={f} last={i === arr.length - 1}
                    onOpen={() => openFile(f)} loading={downloadingFile === f.project_file_id} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const SeverityPill: React.FC<{ s: 'covered' | 'no_commit' | 'no_invites' }> = ({ s }) => {
  if (s === 'covered') return <span style={{ ...pillStyle, background: colors.okBg, color: '#166534' }}><ShieldCheck size={10} /> Covered</span>;
  if (s === 'no_commit') return <span style={{ ...pillStyle, background: colors.warnBg, color: '#92400e' }}><ShieldAlert size={10} /> No commits</span>;
  return <span style={{ ...pillStyle, background: colors.criticalBg, color: '#991b1b' }}><AlertTriangle size={10} /> No invites</span>;
};

const RsvpPill: React.FC<{ s: BidInvite['rsvp_status'] }> = ({ s }) => {
  if (s === 'bid_submitted') return <span style={{ ...pillStyle, background: '#dbeafe', color: '#1d4ed8' }}><Send size={10} /> Submitted</span>;
  if (s === 'will_bid')  return <span style={{ ...pillStyle, background: colors.okBg, color: '#166534' }}><CheckCircle size={10} /> Will bid</span>;
  if (s === 'declining') return <span style={{ ...pillStyle, background: colors.criticalBg, color: '#991b1b' }}><XCircle size={10} /> Declining</span>;
  return <span style={{ ...pillStyle, background: '#f3f4f6', color: '#4b5563' }}><Clock size={10} /> Pending</span>;
};

// Inline company card rendered under each expanded trade row. ONE card per
// company (a single bidder), with each invited contact at that company listed
// underneath. The company-level pill reflects the whole sub's commitment; when
// a sub has multiple contacts, a contact's own RSVP only shows when it
// DIFFERS from the company status — same-status pills on every row were just
// noise (especially since bid_submitted propagates company-wide).
const CompanyCard: React.FC<{ co: { subcontractor_id: number; company_name: string; status: BidInvite['rsvp_status']; contacts: BidInvite[] } }> = ({ co }) => (
  <div style={{
    padding: '12px 14px',
    background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 8,
    display: 'flex', flexDirection: 'column', gap: 8,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, fontWeight: 700, color: colors.text, fontSize: 15 }}>{co.company_name}</div>
      <RsvpPill s={co.status} />
    </div>
    {co.contacts.map((inv, idx) => (
      <div key={inv.invite_id} style={{
        display: 'flex', flexDirection: 'column', gap: 3,
        borderTop: idx > 0 ? `1px solid ${colors.border}` : 'none',
        paddingTop: idx > 0 ? 8 : 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {inv.contact_name && <div style={{ flex: 1, fontSize: 14, color: colors.text }}>{inv.contact_name}</div>}
          {co.contacts.length > 1 && inv.rsvp_status !== co.status && <RsvpPill s={inv.rsvp_status} />}
        </div>
        {inv.contact_email && (
          <a href={`mailto:${inv.contact_email}`} style={{
            fontSize: 14, color: colors.primary, textDecoration: 'underline',
            display: 'inline-flex', alignItems: 'center', gap: 6, wordBreak: 'break-all',
          }}>
            <Mail size={14} /> {inv.contact_email}
          </a>
        )}
        {inv.unsubscribed_at && (
          <div style={{ fontSize: 12, color: colors.critical, fontStyle: 'italic', fontWeight: 600 }}>
            Unsubscribed on {new Date(inv.unsubscribed_at).toLocaleDateString()} — won't receive emails
          </div>
        )}
      </div>
    ))}
  </div>
);

export default BiddingDetail;
